/**
 * InteractiveMode (Plan 01-13, Task 2).
 *
 * Always-mounted surface beneath IdleMode.  Opacity inverse to IdleMode
 * so the two modes cross-fade without unmounting.  Renders the NavShell
 * variant driven by `deviceClass` and React Router's `<Outlet />` for
 * any nested routes Plan 17 wires.
 *
 * Device-class detection runs once on mount via `detectDeviceClass()`
 * — same UA-free (matchMedia only) heuristic Plan 12 uses.  The result
 * lives in the Zustand store so the NavShell re-renders cleanly.
 */
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useDashboard, type DeviceClass } from './DashboardStore';
import { NavShell } from './NavShell';
import { drawsOwnHeader, showsAssistantFab } from './navTabs';
import { ScreenHeader } from '../../components/ScreenHeader';
import { NAV_CLEARANCE, useWideViewport } from '../../lib/useViewport';

/**
 * Heuristic: coarse pointer + ≥900px viewport = tablet; coarse pointer
 * alone = phone; everything else = desktop.  No UA string inspection
 * (D-09 is explicit; this only covers the ambient-dashboard case where
 * the /login modal hasn't yet persisted a choice).
 */
export function detectDeviceClass(): DeviceClass {
  if (typeof window === 'undefined') return 'desktop';
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const wide = window.matchMedia('(min-width: 900px)').matches;
  if (coarse && wide) return 'tablet';
  if (coarse) return 'phone';
  return 'desktop';
}

export function InteractiveMode() {
  const isIdle = useDashboard((s) => s.isIdle);
  const setDeviceClass = useDashboard((s) => s.setDeviceClass);

  useEffect(() => {
    // Device-class detection still seeds the store on first mount (kept for
    // downstream consumers); it no longer drives the nav layout.
    if (useDashboard.getState().deviceClass === 'desktop') {
      setDeviceClass(detectDeviceClass());
    }
  }, [setDeviceClass]);

  // Nav layout is now purely width-driven and reactive to resize (the user
  // wants the rail on PC, the bottom bar on mobile, based on window width):
  //   wide (≥768px) → left rail   |   narrow → bottom pill.
  const wide = useWideViewport();
  const navVariant: 'tablet' | 'phone' = wide ? 'tablet' : 'phone';
  const { pathname } = useLocation();
  // Plass nederst så det siste innholdet kan rulles fri av pillen og
  // assistentknappen. Den brede skinnen tar ingenting nederst, så der trengs
  // plassen bare når knappen vises (ikke på /assistent).
  const bottom = !wide || showsAssistantFab(pathname) ? NAV_CLEARANCE : 0;

  return (
    <div
      data-testid="dashboard-interactive"
      data-mode="interactive"
      // Ingen egen bakgrunn (#151): lerretet med glød er det faste laget i
      // App.tsx, og en flat bg-bg her ville dekket det på alle skjermene.
      className={[
        'text-ink min-h-screen',
        'transition-opacity duration-150',
        // Mens veggskjermen hviler, holdes skallet til én skjermhøyde: dokumentet
        // under IdleMode skal ikke kunne rulles, for innhold som flytter seg
        // under glassflisene tvinger blur-en til å tegnes på nytt hver ramme
        // (perf/card-blur.spec.ts, #152). Toppen og plassen til pillen (#153)
        // gjorde dashbordet høyere enn nettbrettet.
        isIdle
          ? 'opacity-0 pointer-events-none h-dvh overflow-hidden'
          : 'opacity-100 pointer-events-auto',
      ].join(' ')}
    >
      <NavShell variant={navVariant} />
      {/* Each routed page renders its own <main> with its own padding, so
          we don't wrap the Outlet in another <main> (that would nest the
          landmark and double the padding).  We only offset the content
          past the fixed NavShell: the floating rail (16 + 80 + 16 px) on
          wide screens, and the pill + assistant button at the bottom. */}
      <div className={navVariant === 'tablet' ? 'pl-28' : undefined} style={{ paddingBottom: bottom }}>
        {/* #153: Meg er ikke lenger en fane — avataren i toppen er inngangen.
            Skjermene tar ScreenHeader i bruk med egen tittel i sine steg;
            til da står avataren alene over skjermens egen overskrift
            (pt-4 + 44 px). En skjerm med egen topp (Hjem, #154) får ikke
            skallets i tillegg. */}
        {drawsOwnHeader(pathname) ? null : (
          <ScreenHeader data-testid="shell-header" className="px-5 pt-4 md:px-8" />
        )}
        <Outlet />
      </div>
    </div>
  );
}
